/**
 * Demo mode sample resource requests.
 *
 * These mirror the original hardcoded data that existed before the Demands API
 * integration, preserving the full flow for testing in Demo mode.
 */

import type { ResourceRequest } from '@/types/resourceRequest';

export function getDemoResourceRequests(): ResourceRequest[] {
  const today         = new Date();
  const iso           = (d: Date) => d.toISOString().split('T')[0];
  const daysFromNow   = (n: number) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
  const daysAgo       = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  const monthsFromNow = (n: number) => { const d = new Date(today); d.setMonth(d.getMonth() + n); return d; };
  const ym            = (d: Date)   => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

  return [
    {
      id: 'rr-001',
      projectId: 'proj-retail',
      projectName: 'Retail Banking Platform Overhaul',
      projectCode: 'RBP-2025',
      requestedByName: 'James Whitfield',
      requestedBySystem: 'Resource Manager',
      role: 'Senior Developer',
      skills: ['React', 'TypeScript', 'Node.js', 'AWS'],
      totalHoursRequested: 60,
      requestedStartDate: iso(daysFromNow(14)),
      requestedEndDate: iso(monthsFromNow(3)),
      status: 'pending' as const,
      priority: 'high' as const,
      receivedAt: daysAgo(2).toISOString(),
      notes: 'Critical path work for Q3 launch. Needs strong TypeScript and AWS experience.',
      monthlyHourHints: {
        [ym(daysFromNow(14))]:  20,
        [ym(monthsFromNow(1))]: 20,
        [ym(monthsFromNow(2))]: 20,
      },
      location: 'London',
    },
    {
      id: 'rr-002',
      projectId: 'proj-mobile',
      projectName: 'Mobile App Redesign',
      projectCode: 'MAR-2025',
      requestedByName: 'Priya Nair',
      requestedBySystem: 'Salesforce PSA',
      role: 'UI/UX Designer',
      skills: ['Figma', 'User Research', 'Design Systems'],
      totalHoursRequested: 40,
      requestedStartDate: iso(daysFromNow(7)),
      requestedEndDate: iso(monthsFromNow(2)),
      status: 'pending' as const,
      priority: 'medium' as const,
      receivedAt: daysAgo(1).toISOString(),
      notes: 'Discovery and wireframing phase. Ideally someone with mobile design experience.',
      monthlyHourHints: {
        [ym(daysFromNow(7))]:   25,
        [ym(monthsFromNow(1))]: 15,
      },
    },
    {
      id: 'rr-003',
      projectId: 'proj-api',
      projectName: 'API Gateway Migration',
      projectCode: 'AGM-2025',
      requestedByName: 'Tom Briggs',
      requestedBySystem: 'Resource Manager',
      role: 'DevOps Engineer',
      skills: ['Kubernetes', 'Terraform', 'AWS', 'CI/CD'],
      totalHoursRequested: 24,
      requestedStartDate: iso(daysFromNow(3)),
      requestedEndDate: iso(daysFromNow(21)),
      status: 'pending' as const,
      priority: 'critical' as const,
      receivedAt: daysAgo(0).toISOString(),
      notes: 'Production migration window is fixed. Must start within the week.',
      location: 'Remote',
    },
    {
      id: 'rr-004',
      projectId: 'proj-data',
      projectName: 'Data Warehouse Modernisation',
      projectCode: 'DWM-2025',
      requestedByName: 'Laura Chen',
      requestedBySystem: 'Salesforce PSA',
      role: 'Data Analyst',
      skills: ['SQL', 'dbt', 'Snowflake', 'Power BI'],
      totalHoursRequested: 120,
      requestedStartDate: iso(monthsFromNow(1)),
      requestedEndDate: iso(monthsFromNow(5)),
      status: 'pending' as const,
      priority: 'medium' as const,
      receivedAt: daysAgo(3).toISOString(),
      notes: 'Long-running transformation programme. Part-time allocation across a team.',
      monthlyHourHints: {
        [ym(monthsFromNow(1))]: 24,
        [ym(monthsFromNow(2))]: 24,
        [ym(monthsFromNow(3))]: 24,
        [ym(monthsFromNow(4))]: 24,
        [ym(monthsFromNow(5))]: 24,
      },
    },
    {
      id: 'rr-005',
      projectId: 'proj-cx',
      projectName: 'Customer Portal v3',
      projectCode: 'CPV3-2025',
      requestedByName: 'Ami Patel',
      requestedBySystem: 'Resource Manager',
      role: 'Business Analyst',
      skills: ['Requirements Gathering', 'Stakeholder Management', 'JIRA'],
      totalHoursRequested: 32,
      requestedStartDate: iso(daysFromNow(21)),
      requestedEndDate: iso(monthsFromNow(2)),
      status: 'accepted' as const,
      priority: 'low' as const,
      receivedAt: daysAgo(5).toISOString(),
      acceptedScenarioId: 'sc-spread-load',
    },
  ];
}
